/* Concept article page (sections/ac-art-main.liquid).
   - Contents: built from the article's own top-level h2 headings (ids kept
     when the article set them), numbered 01, 02... in the margin rail
     (>= 1280px) and in a bottom sheet behind the phone dock.
   - Progress: one number (0-1) from the prose column, drawn as the top
     bar, the rail meter and the dock line; "n min left" from the word count.
   - Dock (< 1280px): appears once the masthead is gone, hides when the
     closing band is on screen.
   - Molecule (>= 1024px only): the product page's Three.js scene
     (assets/ac-pdp-gl.js + ac-pdp-molecules.js, three.js from the theme's
     own assets), loaded after page load, the first interaction and an idle
     slot. The poster stays when WebGL is unavailable or slow. */
(function () {
  "use strict";

  var root = document.querySelector("[data-ac-art]");
  if (!root) return;

  var THREE_CDN_URL =
    "https://cdn.jsdelivr.net/npm/three@0.160.0/build/three.module.min.js";
  var reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)");
  var saveData = !!(navigator.connection && navigator.connection.saveData);
  var prose = root.querySelector("[data-art-prose]");
  var hero = root.querySelector(".ac-art-hero");
  var close = root.querySelector(".ac-art-close");

  /* ---------------------------------------------------------- */
  /* Headings                                                   */
  /* ---------------------------------------------------------- */

  function slugify(text) {
    var s = String(text || "");
    s = s.normalize ? s.normalize("NFKD") : s;
    return s
      .replace(/[̀-ͯ]/g, "")
      .toLowerCase()
      .replace(/[^a-z0-9\s-]/g, "")
      .trim()
      .replace(/\s+/g, "-")
      .replace(/-+/g, "-")
      .slice(0, 60);
  }

  var used = {};
  Array.prototype.forEach.call(document.querySelectorAll("[id]"), function (el) {
    used[el.id] = true;
  });
  function uniqueId(base) {
    var id = base;
    var n = 1;
    while (used[id]) id = base + "-" + n++;
    used[id] = true;
    return id;
  }

  /* Top-level section headings only: not the ones inside callouts, funnels,
     summaries, tables or the product band. */
  var SKIP =
    "aside, table, .ac-art-band, [class*='callout'], [class*='funnel'], [class*='summary'], .ac-hero-nav, [class*='cta'], [class*='take'], [class*='box'], [class*='note'], [class*='highlight'], [class*='card'], [class*='spotlight'], [class*='calc'], [class*='tool'], [class*='widget']";
  var heads = prose
    ? Array.prototype.filter.call(prose.querySelectorAll("h2"), function (h) {
        var t = (h.textContent || "").trim();
        if (!t || t.length > 140) return false;
        /* Skip headings the page hides (embedded duplicate headlines). */
        if (!h.getClientRects().length) return false;
        var p = h.parentElement && h.parentElement.closest(SKIP);
        return !p || !prose.contains(p);
      })
    : [];

  var faq = root.querySelector(".ac-art-faq");
  /* A "Sources"/"References" heading near the top is a citation block,
     not a section to read through: leave it out of the contents. */
  heads = heads.filter(function (h, i) {
    return !(i < 3 && /^\W*(sources|references|citations)\b/i.test(h.textContent || ""));
  });
  var LEGACY_NUM = /^\s*(\d{1,2}|[ivxl]{1,4})[.)]\s+/i;
  var entries = heads.map(function (h, i) {
    if (!h.id) h.id = uniqueId(slugify(h.textContent) || "section-" + (i + 1));
    var n = (i + 1 < 10 ? "0" : "") + (i + 1);
    var text = h.textContent.trim();
    /* Old headings that carry their own number ("1. What Is...") keep it;
       our number would double it. */
    text = text.replace(/[\u{1F300}-\u{1FAFF}\u{2600}-\u{27BF}\u{2B00}-\u{2BFF}\uFE0F]/gu, "").trim();
    if (LEGACY_NUM.test(text)) text = text.replace(LEGACY_NUM, "");
    /* no section number inside an old card (body-fix marks those) */
    else if (!h.hasAttribute("data-art-incard")) h.setAttribute("data-n", n);
    return { el: h, id: h.id, n: n, text: text };
  });
  var bodyHasFaq = heads.some(function (h) {
    return /frequently asked|faqs?\b/i.test(h.textContent || "");
  });
  if (faq && entries.length && !bodyHasFaq) {
    entries.push({
      el: faq,
      id: faq.id,
      n: "FAQ",
      text: "Frequently asked questions",
    });
  }

  function buildList(list) {
    if (!list) return [];
    var frag = document.createDocumentFragment();
    var links = entries.map(function (e) {
      var li = document.createElement("li");
      var a = document.createElement("a");
      a.href = "#" + e.id;
      a.setAttribute("data-n", e.n);
      a.textContent = e.text;
      li.appendChild(a);
      frag.appendChild(li);
      return a;
    });
    list.appendChild(frag);
    return links;
  }

  var tocBox = root.querySelector("[data-art-toc]");
  var tocLinks = [];
  var sheetLinks = [];
  if (entries.length >= 2) {
    tocLinks = buildList(root.querySelector("[data-art-toc-list]"));
    sheetLinks = buildList(root.querySelector("[data-art-sheet-list]"));
    if (tocBox) tocBox.hidden = false;
  }

  /* ---------------------------------------------------------- */
  /* Sheet                                                      */
  /* ---------------------------------------------------------- */

  var sheet = root.querySelector("[data-art-sheet]");
  var openBtn = root.querySelector("[data-art-sheet-open]");
  var closeBtn = root.querySelector("[data-art-sheet-close]");
  var canSheet = sheet && typeof sheet.showModal === "function" && sheetLinks.length;

  if (openBtn) {
    if (!canSheet) {
      openBtn.addEventListener("click", function () {
        var first = entries[0];
        if (first) first.el.scrollIntoView({ behavior: "smooth", block: "start" });
      });
    } else {
      openBtn.addEventListener("click", function () {
        sheet.showModal();
        var cur = sheet.querySelector('[aria-current="true"]');
        if (cur) cur.scrollIntoView({ block: "center" });
      });
      if (closeBtn)
        closeBtn.addEventListener("click", function () {
          sheet.close();
        });
      sheet.addEventListener("click", function (ev) {
        if (ev.target === sheet) sheet.close();
        var a = ev.target.closest && ev.target.closest("a[href^='#']");
        if (a) {
          ev.preventDefault();
          sheet.close();
          var t = document.getElementById(a.getAttribute("href").slice(1));
          if (t) {
            t.scrollIntoView({
              behavior: reduceMotion.matches ? "auto" : "smooth",
              block: "start",
            });
            history.replaceState(null, "", "#" + t.id);
          }
        }
      });
    }
  }

  /* ---------------------------------------------------------- */
  /* Progress, scroll-spy, dock                                 */
  /* ---------------------------------------------------------- */

  var barTop = root.querySelector("[data-art-progress]");
  var meter = root.querySelector("[data-art-meter]");
  var dockLine = root.querySelector("[data-art-dock-progress]");
  var left = root.querySelector("[data-art-left]");
  var dock = root.querySelector("[data-art-dock]");
  var dockN = root.querySelector("[data-art-dock-n]");
  var dockT = root.querySelector("[data-art-dock-t]");
  /* Same number the masthead prints (Liquid reading_time). */
  var minutes = Math.max(1, parseInt(root.getAttribute("data-minutes"), 10) || 1);

  if (dock && (entries.length >= 2 || dock.querySelector(".ac-art-dock__buy"))) {
    dock.hidden = false;
    if (entries.length < 2) {
      if (openBtn) openBtn.style.visibility = "hidden";
    }
  }

  var current = -1;
  function setCurrent(i) {
    if (i === current) return;
    current = i;
    [tocLinks, sheetLinks].forEach(function (links) {
      links.forEach(function (a, j) {
        if (j === i) a.setAttribute("aria-current", "true");
        else a.removeAttribute("aria-current");
      });
    });
    /* Keep the active item in view inside a long, scrolling contents rail. */
    var active = tocLinks[i];
    var box = root.querySelector("[data-art-toc-list]");
    if (active && box && box.scrollHeight > box.clientHeight) {
      var top = active.getBoundingClientRect().top - box.getBoundingClientRect().top + box.scrollTop;
      if (top < box.scrollTop + 40 || top > box.scrollTop + box.clientHeight - 80)
        box.scrollTop = Math.max(0, top - box.clientHeight / 3);
    }
    if (dockN && dockT) {
      var e = entries[i];
      dockN.textContent = e ? e.n : "";
      dockT.textContent = e ? e.text : "Contents";
    }
  }

  var ticking = false;
  function update() {
    ticking = false;
    var vh = window.innerHeight;
    var p = 0;
    if (prose) {
      var r = prose.getBoundingClientRect();
      var total = r.height - vh * 0.6;
      p = total > 0 ? Math.min(1, Math.max(0, (vh * 0.35 - r.top) / total)) : 0;
    }
    [barTop, meter, dockLine].forEach(function (el) {
      if (el) el.style.setProperty("--p", p.toFixed(4));
    });
    if (left) {
      var m = Math.ceil(minutes * (1 - p));
      left.textContent = p >= 0.99 ? "Finished" : m + " min left";
    }

    var line = vh * 0.3;
    var idx = -1;
    for (var i = 0; i < entries.length; i++) {
      if (entries[i].el.getBoundingClientRect().top <= line) idx = i;
      else break;
    }
    setCurrent(idx);

    if (dock && !dock.hidden) {
      var heroGone = hero ? hero.getBoundingClientRect().bottom < 0 : true;
      var closeIn = close ? close.getBoundingClientRect().top < vh - 40 : false;
      dock.classList.toggle("is-on", heroGone && !closeIn);
    }
  }

  function onScroll() {
    if (!ticking) {
      ticking = true;
      requestAnimationFrame(update);
    }
  }
  window.addEventListener("scroll", onScroll, { passive: true });
  window.addEventListener("resize", onScroll, { passive: true });
  update();

  /* Smooth jumps from the rail. */
  root.addEventListener("click", function (ev) {
    var a = ev.target.closest && ev.target.closest(".ac-art-toc__list a");
    if (!a) return;
    var t = document.getElementById(a.getAttribute("href").slice(1));
    if (!t) return;
    ev.preventDefault();
    t.scrollIntoView({
      behavior: reduceMotion.matches ? "auto" : "smooth",
      block: "start",
    });
    history.replaceState(null, "", "#" + t.id);
  });

  /* ---------------------------------------------------------- */
  /* Share                                                      */
  /* ---------------------------------------------------------- */

  var copy = root.querySelector("[data-art-copy]");
  if (copy) {
    copy.addEventListener("click", function () {
      var url = location.origin + location.pathname;
      var done = function () {
        var label = copy.textContent;
        copy.textContent = copy.getAttribute("data-done") || "Copied";
        copy.classList.add("is-done");
        setTimeout(function () {
          copy.textContent = label;
          copy.classList.remove("is-done");
        }, 2000);
      };
      if (navigator.share && window.matchMedia("(pointer: coarse)").matches) {
        navigator.share({ title: document.title, url: url }).catch(function () {});
        return;
      }
      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(url).then(done, function () {
          window.prompt("Copy this link", url);
        });
      } else {
        window.prompt("Copy this link", url);
      }
    });
  }

  /* Tables on phones: label each cell with its column so assets/ac-art.css can show a wide table as one card
     per row. Only simple tables (a header row, no rowspan/colspan, 3+ columns) are stacked. Runs at once, not
     in whenReady: it changes layout. */
  function stackTables() {
    document.querySelectorAll(".ac-art-prose .ac-art-table table").forEach(function (t) {
      if (t.classList.contains("ac-art-stack") || t.querySelector("[rowspan], [colspan]")) return;
      var head = t.tHead && t.tHead.rows[0];
      var body = t.tBodies[0];
      if (!head && t.rows[0] && t.rows[0].querySelector("th") && !t.rows[0].querySelector("td")) head = t.rows[0];
      if (!head || !body || head.cells.length < 3) return;
      var labels = Array.prototype.map.call(head.cells, function (c) {
        return c.textContent.trim();
      });
      Array.prototype.forEach.call(body.rows, function (tr) {
        if (tr === head) return;
        Array.prototype.forEach.call(tr.cells, function (td, i) {
          if (labels[i]) td.setAttribute("data-label", labels[i]);
        });
      });
      t.classList.add("ac-art-stack");
      if (head.parentNode === body) head.classList.add("ac-art-stack__head");
    });
  }
  /* before the molecule block below: it returns early on phones and pages without a molecule */
  stackTables();

  /* ---------------------------------------------------------- */
  /* Molecule                                                   */
  /* ---------------------------------------------------------- */

  var mol = root.querySelector("[data-art-molecule]");
  if (
    !mol ||
    reduceMotion.matches ||
    saveData ||
    !window.WebGL2RenderingContext ||
    !window.matchMedia("(min-width: 1024px)").matches
  )
    return;

  function loadModule(url) {
    /* Shopify's minifier rewrites a literal import(); keep it in a Function. */
    return Promise.resolve().then(function () {
      return new Function("u", "return import(u)")(url);
    });
  }

  var scripts = {};
  function loadScript(src) {
    if (!scripts[src]) {
      scripts[src] = new Promise(function (resolve, reject) {
        var s = document.createElement("script");
        s.src = src;
        s.async = true;
        s.onload = function () {
          resolve();
        };
        s.onerror = function () {
          reject(new Error("could not load " + src));
        };
        document.head.appendChild(s);
      });
    }
    return scripts[src];
  }

  function start() {
    var stage = mol.querySelector(".ac-art-mol__stage");
    var canvas = mol.querySelector("canvas");
    var own = root.getAttribute("data-three-src");
    var three = (own ? loadModule(own) : Promise.reject(new Error("no own three")))
      .catch(function () {
        return loadModule(THREE_CDN_URL);
      });
    Promise.all([
      three,
      loadScript(mol.getAttribute("data-gl-src")),
      loadScript(mol.getAttribute("data-molecules-src")),
    ])
      .then(function (mods) {
        if (!window.AC_PDP_GL || !window.AC_PDP_MOLECULES) return;
        window.AC_PDP_GL.molecule(mods[0], {
          box: stage,
          canvas: canvas,
          data: window.AC_PDP_MOLECULES,
          key: mol.getAttribute("data-molecule"),
          test: false,
          onFail: function () {},
        });
      })
      .catch(function () {
        /* The poster is already the picture. */
      });
  }

  function whenReady(fn) {
    var fired = false;
    var events = ["pointermove", "pointerdown", "scroll", "keydown", "touchstart", "wheel"];
    function go() {
      if (fired) return;
      fired = true;
      events.forEach(function (e) {
        window.removeEventListener(e, go, true);
      });
      if ("requestIdleCallback" in window) window.requestIdleCallback(fn, { timeout: 2500 });
      else setTimeout(fn, 600);
    }
    function arm() {
      events.forEach(function (e) {
        window.addEventListener(e, go, { capture: true, passive: true, once: true });
      });
    }
    if (document.readyState === "complete") arm();
    else window.addEventListener("load", arm, { once: true });
  }

  whenReady(start);
})();
